import React, { useEffect, useMemo, useState } from 'react';
import { Card, Checkbox, Form, Input, Select, Space, Typography, message } from 'antd';
import type { QaqcPipeInspector, Company, VwPersonnel } from '../../../../types';
import { useGetCompanies, useGetPersonnel, useGetQaqcPipeInspector, useGetProfessions } from '../../../../hooks/genericHooks';
import { insertData, updateData, getTableNextSequenceValue } from '../../../../services/api';
import Button from '../../../../components/common/Button';

const { Title, Text } = Typography;

export interface QaqcPipeInspectorCreateProps {
  mode?: 'create' | 'edit';
  initialValues?: Partial<QaqcPipeInspector>;
  onSaved?: (rec: Partial<QaqcPipeInspector>) => void;
  onCancel?: () => void;
  onBack?: () => void;
  viewOnly?: boolean;
}

const employmentStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'terminated', label: 'Terminated' },
];

const QaqcPipeInspectorCreate: React.FC<QaqcPipeInspectorCreateProps> = ({
  mode = 'create',
  initialValues,
  onSaved,
  onCancel,
  onBack,
  viewOnly,
}) => {
  const [form] = Form.useForm<QaqcPipeInspector>();
  const isEdit = mode === 'edit';
  const isViewOnly = !!viewOnly;

  const { data: companiesResp } = useGetCompanies();
  const { data: personnelResp } = useGetPersonnel();
  const { data: professionsResp } = useGetProfessions();

  const companies = (companiesResp?.data as unknown as Company[]) || [];
  const personnel = (personnelResp?.data as unknown as VwPersonnel[]) || [];
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined);
  const [selectedOccupation, setSelectedOccupation] = useState<string | undefined>(undefined);

  const companyOptions = useMemo(() => companies.map(c => ({ value: c.id, label: c.name })), [companies]);
  const occupationOptions = useMemo(() => {
    const list = (professionsResp?.data as any[]) || [];
    const set = new Set<string>();
    list.forEach((p: any) => { if (p?.profession) set.add(String(p.profession)); });
    return Array.from(set).sort((a,b)=>a.localeCompare(b)).map(o => ({ value: o, label: o }));
  }, [professionsResp]);
  type PersOption = { value: string; label: string; meta: VwPersonnel };
  const personnelOptions: PersOption[] = useMemo(() => {
    let list = personnel as VwPersonnel[];
    if (selectedCompanyId) list = list.filter(p => (p.company_id ?? null) === selectedCompanyId);
    if (selectedOccupation) list = list.filter(p => (p.occupation || '').toLowerCase() === selectedOccupation.toLowerCase());
    return list.map(p => ({ value: p.pers_id, label: `${p.pers_id} - ${p.first_name} ${p.last_name}`.trim(), meta: p }));
  }, [personnel, selectedCompanyId, selectedOccupation]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({ ...initialValues } as any);
    } else {
      // Suggest next inspector code like INS-00001 (peek without consuming the sequence)
      (async () => {
        try {
          const nextVal = await getTableNextSequenceValue('qaqc_pipe_inspector', 'id');
          if (Number.isFinite(nextVal)) {
            const code = `INS-${String(nextVal as number).padStart(5, '0')}`;
            const current = form.getFieldValue('inspector_name');
            if (!current) form.setFieldsValue({ inspector_name: code } as any);
          }
        } catch {}
      })();
    }
  }, [initialValues, form]);

  const onSubmit = async () => {
    try {
      // Ensure we have an inspector code; if missing, generate a fresh suggestion
      let ins = form.getFieldValue('inspector_name');
      if (!ins) {
        const nextVal = await getTableNextSequenceValue('qaqc_pipe_inspector', 'id');
        if (Number.isFinite(nextVal)) {
          ins = `INS-${String(nextVal as number).padStart(5, '0')}`;
          form.setFieldsValue({ inspector_name: ins } as any);
        }
      }
      const values = await form.validateFields();
      const payload: any = {
        pers_id: values.pers_id,
        first_name: values.first_name,
        last_name: values.last_name,
        company_id: values.company_id,
        inspector_name: values.inspector_name,
        employment_status: values.employment_status,
        bevel_check: !!values.bevel_check,
        visual_check: !!values.visual_check,
        pwht: !!values.pwht,
        pmi: !!values.pmi,
        pt: !!values.pt,
        rt: !!values.rt,
        mt: !!values.mt,
        ut: !!values.ut,
        test_package: !!values.test_package,
        line_check: !!values.line_check,
        cleaning_package: !!values.cleaning_package,
        flushing_package: !!values.flushing_package,
      } as QaqcPipeInspector;
      if (isEdit && initialValues?.id) {
        await updateData('qaqc_pipe_inspector', initialValues.id, payload, 'id');
        message.success('Inspector updated');
        try { window.dispatchEvent(new Event('qaqc_pipe_inspector:changed')); } catch {}
        onSaved?.({ id: initialValues.id, ...payload });
      } else {
        const resp = await insertData('qaqc_pipe_inspector', payload);
        message.success('Inspector created');
        try { window.dispatchEvent(new Event('qaqc_pipe_inspector:changed')); } catch {}
        onSaved?.({ id: resp?.ids?.[0], ...payload });
        form.resetFields();
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || 'Save failed');
    }
  };

  // Auto fill first/last name when personnel selected
  const handlePersonnelChange = (val?: string) => {
    const p = personnelOptions.find(o => o.value === val)?.meta;
    form.setFieldsValue({
      pers_id: val,
      first_name: p?.first_name || undefined,
      last_name: p?.last_name || undefined,
      company_id: p?.company_id ?? form.getFieldValue('company_id'),
      employment_status: p?.employment_status ?? form.getFieldValue('employment_status'),
    } as any);
  };

  const uiSizes = { titleFontSize: 18 } as const;

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: uiSizes.titleFontSize }}>
            {(isEdit || isViewOnly) && onBack && (
              <a onClick={onBack} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            )}
            {isViewOnly ? 'View Inspector' : (isEdit ? 'Edit Inspector' : 'Create Inspector')}
          </span>
        </div>
      }
      bordered
      size="small"
    >
      <Form form={form} layout="vertical" initialValues={initialValues as any}>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Space style={{ gap: 12, display: 'flex', flexWrap: 'wrap' }}>
            <Form.Item label="Company" name="company_id" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
              <Select
                allowClear
                showSearch
                options={companyOptions}
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                style={{ width: 260 }}
                disabled={isEdit || isViewOnly}
                value={selectedCompanyId}
                onChange={(v) => {
                  const val = (v as number | undefined) ?? undefined;
                  setSelectedCompanyId(val);
                  // Clear dependent personnel when company changes
                  form.setFieldsValue({ pers_id: undefined } as any);
                }}
                onClear={() => {
                  setSelectedCompanyId(undefined);
                  form.setFieldsValue({ company_id: undefined, pers_id: undefined } as any);
                }}
              />
            </Form.Item>
            <Form.Item label="Occupation" style={{ marginBottom: 0 }}>
              <Select
                allowClear
                showSearch
                options={occupationOptions}
                value={selectedOccupation}
                onChange={(v) => {
                  const val = v ? String(v) : undefined;
                  setSelectedOccupation(val);
                  form.setFieldsValue({ pers_id: undefined } as any);
                }}
                onClear={() => {
                  setSelectedOccupation(undefined);
                  form.setFieldsValue({ pers_id: undefined } as any);
                }}
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                placeholder="Filter occupation"
                style={{ width: 200 }}
                disabled={isEdit || isViewOnly}
              />
            </Form.Item>
            <Form.Item label="Personnel" name="pers_id" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
              <Select
                allowClear
                showSearch
                options={personnelOptions}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = String(option?.label || '').toLowerCase();
                  const tokens = input.trim().toLowerCase().split(/\s+/).filter(Boolean);
                  return tokens.every(tok => label.includes(tok));
                }}
                onChange={handlePersonnelChange}
                style={{ width: 300 }}
                disabled={isEdit || isViewOnly}
              />
            </Form.Item>
            <Form.Item label="Inspector Name" name="inspector_name" rules={[{ required: true, max: 30 }]} style={{ marginBottom: 0 }}>
              <Input maxLength={30} placeholder="Auto-generated" disabled style={{ width: 160 }} />
            </Form.Item>
            <Form.Item label="Employment Status" name="employment_status" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
              <Select options={employmentStatusOptions} disabled={!isEdit || isViewOnly} style={{ width: 160 }} />
            </Form.Item>
          </Space>

          <Card size="small" title={<Text strong>Capabilities</Text>}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Space size={16} wrap>
                <Form.Item name="bevel_check" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Bevel Check</Checkbox></Form.Item>
                <Form.Item name="visual_check" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Visual Check</Checkbox></Form.Item>
                <Form.Item name="pwht" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>PWHT</Checkbox></Form.Item>
                <Form.Item name="pmi" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>PMI</Checkbox></Form.Item>
              </Space>
              <Space size={16} wrap>
                <Form.Item name="pt" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Penetrant Test</Checkbox></Form.Item>
                <Form.Item name="rt" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Radiographic Test</Checkbox></Form.Item>
                <Form.Item name="mt" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Magnetic Particle Test</Checkbox></Form.Item>
                <Form.Item name="ut" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Ultrasonic Test</Checkbox></Form.Item>
              </Space>
              <Space size={16} wrap>
                <Form.Item name="test_package" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Test Package</Checkbox></Form.Item>
                <Form.Item name="line_check" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Line Check</Checkbox></Form.Item>
                <Form.Item name="cleaning_package" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Cleaning Package</Checkbox></Form.Item>
                <Form.Item name="flushing_package" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox disabled={isViewOnly}>Flushing Package</Checkbox></Form.Item>
              </Space>
            </Space>
          </Card>

          {!isViewOnly && (
            <Space>
              <Button action={isEdit ? 'edit' : 'add'} type="primary" onClick={onSubmit}>{isEdit ? 'Update' : 'Create'}</Button>
              {onCancel && <Button onClick={onCancel}>Cancel</Button>}
            </Space>
          )}
        </Space>
      </Form>
    </Card>
  );
};

export default QaqcPipeInspectorCreate;
